import { BaseIdentityClient, BaseIdentityError, decodeAccessToken, type ApiEnvelope, type SessionResponse } from "@base-identity/react";
import type { ApiResponse, User } from "@/types/apiTypes";

export const identityBaseUrl = (import.meta.env.VITE_IDENTITY_URL || "/identity").replace(/\/$/, "");
export const identityPublishableKey = import.meta.env.VITE_BASE_IDENTITY_PUBLISHABLE_KEY || undefined;

export const identityClient = new BaseIdentityClient({
  baseUrl: identityBaseUrl,
  publishableKey: identityPublishableKey,
  onSignedOut: () => {
    if (/^\/(user|admin|superadmin)(?:\/|$)/.test(window.location.pathname)) {
      const intended = `${window.location.pathname}${window.location.search}`;
      window.location.assign(`/login?redirect=${encodeURIComponent(intended)}`);
    }
  },
});

export function identityHeaders(extra: Record<string, string> = {}): Headers {
  const headers = new Headers({ Accept: "application/json", ...extra });
  if (identityPublishableKey) headers.set("X-Base-Identity-Publishable-Key", identityPublishableKey);
  return headers;
}

export class IdentityRequestError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
    readonly status: number,
  ) {
    super(message);
    this.name = "IdentityRequestError";
  }
}

export function passwordResetErrorMessage(cause: unknown, fallback: string): string {
  if (cause instanceof IdentityRequestError) {
    if (cause.code === "VERIFICATION_DELIVERY_UNAVAILABLE") {
      return "לא ניתן לשלוח את הודעת האימות כרגע. אפשר לנסות שוב בעוד כמה דקות.";
    }
    if (cause.code === "TRY_AGAIN_LATER") {
      return "נשלח קוד לאחרונה. אפשר לנסות שוב בעוד כמה שניות.";
    }
  }
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

async function readIdentityEnvelope<T>(response: Response, fallback: string): Promise<T> {
  const raw = await response.text();
  let envelope: ApiEnvelope<T> | null = null;
  if (raw) {
    try {
      envelope = JSON.parse(raw) as ApiEnvelope<T>;
    } catch {
      throw new IdentityRequestError(`${fallback} (HTTP ${response.status})`, undefined, response.status);
    }
  }
  if (!response.ok || !envelope?.success) {
    const error = envelope && !envelope.success ? envelope.error : undefined;
    throw new IdentityRequestError(error?.message || fallback, error?.code, response.status);
  }
  return envelope.data;
}

async function identityJSON<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(`${identityBaseUrl}${path}`, {
    ...init,
    credentials: "include",
    headers: identityHeaders({ "Content-Type": "application/json", ...(init.headers as Record<string, string> | undefined) }),
  });
	return readIdentityEnvelope<T>(response, "Identity request failed");
}

async function identityAuthJSON<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await identityClient.authFetch(`${identityBaseUrl}${path}`, {
    ...init,
    headers: identityHeaders({ "Content-Type": "application/json", ...(init.headers as Record<string, string> | undefined) }),
  });
	return readIdentityEnvelope<T>(response, "Identity request failed");
}

export type AuthenticationStrategy = {
  id: string;
  type: "email_code" | "password" | "oauth";
  start_url?: string;
};

export async function listAuthenticationStrategies(): Promise<AuthenticationStrategy[]> {
  const result = await identityJSON<{ strategies: AuthenticationStrategy[] }>("/v1/auth/strategies", { method: "GET" });
  return result.strategies;
}

export type IdentityProfile = {
  user_id: string;
  primary_email: string;
  first_name?: string | null;
  last_name?: string | null;
  image_url?: string | null;
  public_metadata: Record<string, unknown> | null;
  password_configured: boolean;
};

export type RequiredIdentityProfileField = "first_name" | "last_name" | "phone_number";

export class ProfileCompletionRequiredError extends Error {
  constructor() {
    super("יש להשלים שם פרטי, שם משפחה ומספר טלפון כדי להמשיך");
    this.name = "ProfileCompletionRequiredError";
  }
}

export function safeRequestedPath(value: string | null): string | null {
  if (!value?.startsWith("/") || /^\/[\\/]/.test(value) || Array.from(value).some((char) => char.charCodeAt(0) < 32)) return null;
  if (/^\/(login|signup|oauth|logout)(?:[/?#]|$)/.test(value)) return null;
  return value;
}

export function profileCompletionPath(redirect: string | null = null): string {
  const target = safeRequestedPath(redirect);
  return `/oauth/complete-profile${target ? `?redirect=${encodeURIComponent(target)}` : ""}`;
}

const PHONE_NUMBER_PATTERN = /^\+?[0-9]{9,15}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requiredText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizeIdentityPhoneNumber(value: unknown): string {
  return requiredText(value).replace(/[\s()-]/g, "");
}

export function getIdentityPhoneNumber(profile: IdentityProfile): string {
  const zoooz = profile.public_metadata?.zoooz;
  return isRecord(zoooz) ? normalizeIdentityPhoneNumber(zoooz.phone_number) : "";
}

export function missingRequiredIdentityProfileFields(profile: IdentityProfile): RequiredIdentityProfileField[] {
  const missing: RequiredIdentityProfileField[] = [];
  const firstName = requiredText(profile.first_name);
  const lastName = requiredText(profile.last_name);

  if (!firstName || firstName === "-") missing.push("first_name");
  if (!lastName || lastName === "-") missing.push("last_name");
  if (!PHONE_NUMBER_PATTERN.test(getIdentityPhoneNumber(profile))) missing.push("phone_number");
  return missing;
}

export function identityProfileIsComplete(profile: IdentityProfile): boolean {
  return missingRequiredIdentityProfileFields(profile).length === 0;
}

export async function startEmailSignIn(email: string): Promise<void> {
  await identityJSON<{ accepted: boolean }>("/v1/auth/login/email", { method: "POST", body: JSON.stringify({ email }) });
}

export async function confirmEmailSignIn(email: string, code: string): Promise<SessionResponse> {
  return identityJSON<SessionResponse>("/v1/auth/login/email/confirm", { method: "POST", body: JSON.stringify({ email, code }) });
}

export async function passwordSignIn(email: string, password: string): Promise<SessionResponse> {
  return identityJSON<SessionResponse>("/v1/auth/login/password", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function beginOAuth(strategy: AuthenticationStrategy, returnTo: string): Promise<string> {
  if (strategy.type !== "oauth" || !strategy.start_url?.startsWith("/v1/auth/oauth/")) {
    throw new Error("OAuth strategy is invalid");
  }
  const separator = strategy.start_url.includes("?") ? "&" : "?";
  const result = await identityJSON<{ authorization_url: string }>(
    `${strategy.start_url}${separator}return_to=${encodeURIComponent(returnTo)}`,
    { method: "GET" },
  );
  return result.authorization_url;
}

export async function startRegistration(email: string): Promise<void> {
  await identityJSON<{ accepted: boolean }>("/v1/auth/register", { method: "POST", body: JSON.stringify({ email }) });
}

export async function confirmRegistration(email: string, code: string): Promise<SessionResponse> {
  return identityJSON<SessionResponse>("/v1/auth/register/confirm", { method: "POST", body: JSON.stringify({ email, code }) });
}

export async function startPasswordReset(email: string): Promise<void> {
  try {
    await identityClient.startPasswordReset({ email });
  } catch (cause) {
    if (cause instanceof BaseIdentityError) {
      throw new IdentityRequestError(cause.message, cause.code, cause.status ?? 0);
    }
    throw cause;
  }
}

export async function confirmPasswordReset(email: string, code: string, password: string): Promise<void> {
  try {
    await identityClient.confirmPasswordReset({ email, code, password });
  } catch (cause) {
    if (cause instanceof BaseIdentityError) {
      throw new IdentityRequestError(cause.message, cause.code, cause.status ?? 0);
    }
    throw cause;
  }
}

export async function completeIdentityProfile(input: {
	firstName: string;
  lastName: string;
  phoneNumber: string;
}): Promise<User> {
	const session = identityClient.getSnapshot();
	const currentProfile = await getIdentityProfile();
	assertCurrentSession(session);
	const currentZoooz = isRecord(currentProfile.public_metadata?.zoooz)
		? currentProfile.public_metadata.zoooz
		: {};
	await identityAuthJSON<IdentityProfile>("/v1/me/profile", {
		method: "PATCH",
		body: JSON.stringify({
			first_name: input.firstName.trim(),
      last_name: input.lastName.trim(),
      public_metadata: {
				...currentProfile.public_metadata,
				zoooz: { ...currentZoooz, phone_number: normalizeIdentityPhoneNumber(input.phoneNumber) },
			},
		}),
	});
	assertCurrentSession(session);

  return ensureZooozProfile();
}

/** Creates the Zoooz read-model user on first Identity login and is safe to
 * call again for existing users. This is required before any /api/user route
 * can resolve a brand-new OAuth identity to its Mongo user. */
const profileSetups = new Map<string, Promise<User>>();

export async function ensureZooozProfile(): Promise<User> {
  const session = identityClient.getSnapshot();
  if (!session.userId || !session.sessionId) throw new Error("Authentication required");
  const key = `${session.userId}:${session.sessionId}`;
  const existing = profileSetups.get(key);
  if (existing) return existing;
  const pending = (async () => {
    const response = await identityClient.authFetch("/api/user/onboarding", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: "{}",
    });
    const envelope = await response.json() as ApiResponse<{ user: User }>;
    assertCurrentSession(session);
    if (envelope.error?.code === "PROFILE_COMPLETION_REQUIRED") throw new ProfileCompletionRequiredError();
    if (!response.ok || !envelope.data?.user) throw new Error(envelope.error?.message || "Zoooz profile setup failed");
    return envelope.data.user;
  })().finally(() => { profileSetups.delete(key); });
  profileSetups.set(key, pending);
  return pending;
}

export async function getIdentityProfile(): Promise<IdentityProfile> {
  const session = identityClient.getSnapshot();
  const profile = await identityAuthJSON<IdentityProfile>("/v1/me/profile", { method: "GET" });
  assertCurrentSession(session);
  if (profile.user_id !== session.userId) throw new Error("Identity profile does not match the current session");
  return profile;
}

function assertCurrentSession(session: { userId: string | null; sessionId: string | null }): void {
  const current = identityClient.getSnapshot();
  if (!session.userId || !session.sessionId || current.userId !== session.userId || current.sessionId !== session.sessionId) {
    throw new Error("Session changed while loading profile");
  }
}

export async function updateIdentityPublicMetadata(publicMetadata: Record<string, unknown>): Promise<IdentityProfile> {
  return identityAuthJSON<IdentityProfile>("/v1/me/profile", {
    method: "PATCH",
    body: JSON.stringify({ public_metadata: publicMetadata }),
  });
}

export async function setInitialPassword(password: string): Promise<void> {
  await identityAuthJSON<{ configured: boolean }>("/v1/me/password", {
    method: "PUT",
    body: JSON.stringify({ password }),
  });
}

const profileLoads = new Map<string, Promise<User>>();

export async function loadZooozProfile(): Promise<User> {
  const session = identityClient.getSnapshot();
  if (!session.userId || !session.sessionId) throw new Error("Authentication required");
  const key = `${session.userId}:${session.sessionId}`;
  const existing = profileLoads.get(key);
  if (existing) return existing;
  const pending = (async () => {
    // Webhooks may already have created a local user with placeholder fields.
    // Always check the authoritative profile before considering login complete.
    const profile = await getIdentityProfile();
    assertCurrentSession(session);
    if (!identityProfileIsComplete(profile)) throw new ProfileCompletionRequiredError();
    const response = await identityClient.authFetch("/api/user/me", { headers: { Accept: "application/json" } });
    const envelope = await response.json() as ApiResponse<{ user: User }>;
    assertCurrentSession(session);
    if (envelope.error?.code === "PROFILE_COMPLETION_REQUIRED") throw new ProfileCompletionRequiredError();
    const user = response.status === 404 && envelope.error?.message === "Zoooz profile is not migrated"
      ? await ensureZooozProfile()
      : response.ok && envelope.data?.user;
    if (!user) throw new Error(envelope.error?.message || `Zoooz profile is unavailable (HTTP ${response.status})`);
    assertCurrentSession(session);
    return user;
  })().finally(() => { profileLoads.delete(key); });
  profileLoads.set(key, pending);
  return pending;
}

export function rolePathFromAccessToken(accessToken: string): "user" | "admin" | "superadmin" {
  const roles = decodeAccessToken(accessToken)?.roles ?? [];
  if (roles.includes("zoooz_superadmin")) return "superadmin";
  if (roles.includes("zoooz_admin")) return "admin";
  return "user";
}

export function rolePathFromRoles(roles: string[] | undefined): "user" | "admin" | "superadmin" {
  if (roles?.includes("zoooz_superadmin")) return "superadmin";
  if (roles?.includes("zoooz_admin")) return "admin";
  return "user";
}
